import { useState, useEffect } from "react";
import { ChatInterface } from "./components/ChatInterface";
import { Sidebar } from "./components/Sidebar";
import { ModelSelector } from "./components/ModelSelector";
import { ConnectionStatus } from "./components/ConnectionStatus";
import { ThemeProvider } from "./components/ThemeProvider";
import { ThemeToggle } from "./components/ThemeToggle";
import { CustomTitleBar } from "./components/CustomTitleBar";
import { ChatMode, Thread } from "./types";
import { threadService } from "./services/threadService";
import { aiService } from "./services/aiService";

function App() {
  console.log("React: App component mounting...");
  const [isConnected, setIsConnected] = useState(false);
  const [models, setModels] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [chatMode, setChatMode] = useState<ChatMode>("general");
  const [isLoading, setIsLoading] = useState(true);

  // Thread management
  const [threads, setThreads] = useState<Thread[]>([]);
  const [currentThread, setCurrentThread] = useState<Thread | null>(null);

  useEffect(() => {
    console.log("React: useEffect running, calling checkConnection...");
    checkConnection();
    loadThreads();
  }, []);

  const checkConnection = async () => {
    try {
      console.log("React: Checking Ollama connection...");
      const connected = await aiService.checkConnection();
      console.log("React: Connection result:", connected);
      setIsConnected(connected);

      if (connected) {
        await loadModels();
      }
    } catch (error) {
      console.error("React: Connection check failed:", error);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadModels = async () => {
    try {
      console.log("React: Loading models...");
      const modelList = await aiService.listModels();
      console.log("React: Models loaded:", modelList);
      setModels(modelList);

      if (modelList.length > 0 && !selectedModel) {
        setSelectedModel(modelList[0].name);
      }
    } catch (error) {
      console.error("React: Failed to load models:", error);
    }
  };

  const loadThreads = () => {
    const loadedThreads = threadService.getThreads();
    setThreads(loadedThreads);

    // Select the first thread if none is selected
    if (loadedThreads.length > 0 && !currentThread) {
      setCurrentThread(loadedThreads[0]);
    }
  };

  const handleModelChange = (modelName: string) => {
    setSelectedModel(modelName);

    // Update current thread's model if it exists
    if (currentThread) {
      const updatedThread = {
        ...currentThread,
        settings: { ...currentThread.settings, selectedModel: modelName },
      };
      threadService.updateThread(currentThread.id, updatedThread);
      setCurrentThread(updatedThread);
      loadThreads(); // Refresh threads
    }
  };

  const handleModeChange = (mode: ChatMode) => {
    setChatMode(mode);
  };

  const handleNewThread = () => {
    const threadName = "New General Chat";
    const newThread = threadService.createThread(threadName, chatMode, {
      selectedModel,
    });
    setCurrentThread(newThread);
    loadThreads();
  };

  const handleThreadSelect = (thread: Thread) => {
    setCurrentThread(thread);
    setChatMode(thread.chatMode);
    setSelectedModel(thread.settings.selectedModel);
  };

  const handleDeleteThread = (threadId: string) => {
    threadService.deleteThread(threadId);
    loadThreads();

    // If we deleted the current thread, select the first available one
    if (currentThread?.id === threadId) {
      const remainingThreads = threadService.getThreads();
      setCurrentThread(
        remainingThreads.length > 0 ? remainingThreads[0] : null
      );
    }
  };

  const handleThreadUpdate = (updatedThread: Thread) => {
    threadService.updateThread(updatedThread.id, updatedThread);
    setCurrentThread(updatedThread);
    loadThreads();
  };

  if (isLoading) {
    return (
      <ThemeProvider>
        <div className='h-screen flex items-center justify-center'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4'></div>
            <p className='text-gray-600 dark:text-gray-400'>
              Connecting to Ollama...
            </p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className='h-screen flex flex-col'>
        <CustomTitleBar>
          <div className='flex items-center space-x-4'>
            <ConnectionStatus isConnected={isConnected} />
            <ModelSelector
              models={models}
              selectedModel={selectedModel}
              onModelChange={handleModelChange}
              disabled={!isConnected}
            />
            <ThemeToggle />
          </div>
        </CustomTitleBar>
        <div className='flex flex-row flex-1 overflow-hidden'>
          <Sidebar
            threads={threads}
            currentThread={currentThread}
            onThreadSelect={handleThreadSelect}
            onNewThread={handleNewThread}
            onDeleteThread={handleDeleteThread}
            chatMode={chatMode}
            onModeChange={handleModeChange}
            isConnected={isConnected}
            onRetryConnection={checkConnection}
          />

          <main className='flex-1 overflow-hidden'>
            <ChatInterface
              thread={currentThread}
              onThreadUpdate={handleThreadUpdate}
              isConnected={isConnected}
            />
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}

export default App;
